import { plaqueOptions } from "../plaqueConfigurations/plaque-configurations";
import { PlaqueOptionKeys } from "../plaque-option-keys";
import { PlaqueOptions } from "../types";
import {
  MembershipLevels,
  PlaqueTypes,
  ProductsWithInitials,
  TemplateType,
} from "../../../utils/constants/constants";
import { Product } from "../../../types/internal.types";

export const isPlaqueWithYearSumInsert = (type: TemplateType) =>
  type === PlaqueTypes.HONOR_ROLL || type === PlaqueTypes.QUARTER_CENTURY;

export const isPlaqueWithMultipleYearInserts = (type: TemplateType) =>
  type === PlaqueTypes.COURT_OF_THE_TABLE_YEAR_PLATE ||
  type === PlaqueTypes.P1404_COURT_OF_THE_TABLE_YEAR_PLATE_PLAQUE ||
  type === PlaqueTypes.TOP_OF_THE_TABLE_YEAR_PLATE ||
  type === PlaqueTypes.P1407_TOP_OF_THE_TABLE_YEAR_PLATE_PLAQUE ||
  type === PlaqueTypes.BRONZE_LIFE_MEMBER;

export const isPlaqueWithMembershipType = (type: TemplateType) =>
  type === PlaqueTypes.BLACK_PIANO_BOARD ||
  type === PlaqueTypes.CONTEMPORARY_BLUE_MIRROR ||
  type === PlaqueTypes.CONTEMPORARY_MIRROR ||
  type === PlaqueTypes.P1303_CONTEMPORARY_MIRROR_PLAQUE ||
  type === PlaqueTypes.P1310_CONTEMPORARY_BLUE_MIRROR_PLAQUE ||
  type === PlaqueTypes.SHADOWBOX_SHIELD ||
  type === PlaqueTypes.BLACK_PIANO_DESKTOP ||
  type === PlaqueTypes.CHERRYWOOD ||
  type === PlaqueTypes.P1307_ROSEWOOD_PORTRAIT_PLAQUE ||
  type === PlaqueTypes.P1308_BLACK_PORTRAIT_PLAQUE ||
  type === PlaqueTypes.ROSEWOOD_DESKTOP ||
  type === PlaqueTypes.SMOKED_GLASS ||
  type === PlaqueTypes.P1300_SMOKED_GLASS_SILVER_PLAQUE ||
  type === PlaqueTypes.P413_ALUMINUM_PLAQUE ||
  type === PlaqueTypes.MEMBER_CRYSTAL_CUBE ||
  type === PlaqueTypes.P1405_BLACK_PIANO_DESKTOP_PLAQUE ||
  type === PlaqueTypes.P1406_ROSEWOOD_PIANO_DESKTOP_PLAQUE ||
  type === PlaqueTypes.BRONZE_LIFE_MEMBER;

export const isPlaqueWithMemberPhoto = (type: TemplateType) =>
  type === PlaqueTypes.BLACK_PIANO_BOARD ||
  type === PlaqueTypes.CHERRYWOOD ||
  type === PlaqueTypes.P1307_ROSEWOOD_PORTRAIT_PLAQUE ||
  type === PlaqueTypes.P1308_BLACK_PORTRAIT_PLAQUE;

export const isYearSumPlateInsert = (type: TemplateType) =>
  type === PlaqueTypes.QUARTER_CENTURY_YEAR_PLATE_INSERT ||
  type === PlaqueTypes.HONOR_ROLL_YEAR_PLATE_INSERT;

export const isYearPlateInsert = (type: TemplateType) =>
  type === PlaqueTypes.COURT_OF_THE_TABLE_YEAR_PLATE_INSERT ||
  type === PlaqueTypes.TOP_OF_THE_TABLE_YEAR_PLATE_INSERT;

export const isCertificate = (type: TemplateType) =>
  type === PlaqueTypes.CERTIFICATE;

export const isLeftAligned = (type: TemplateType) =>
  type === PlaqueTypes.CERTIFICATE;

export const isNameUpperCase = (type: TemplateType) =>
  type !== PlaqueTypes.CERTIFICATE &&
  type !== PlaqueTypes.TOP_OF_THE_TABLE_DIAMOND_CUT_CRYSTAL &&
  type !== PlaqueTypes.MEMBER_CRYSTAL_CUBE;

export const isMembershipTypeUpperCase = (type: TemplateType) =>
  type === PlaqueTypes.BRONZE_LIFE_MEMBER;

export const getOptionValue = <K extends keyof PlaqueOptions>(
  type: TemplateType,
  optionKey: K
): PlaqueOptions[K] | undefined => {
  const options = plaqueOptions.find((option) => option.name === type);

  return options?.[optionKey];
};

export const getColor = (type: TemplateType) =>
  getOptionValue(type, PlaqueOptionKeys.Color);

export const hasMembershipLevel = (
  membershipLevel: MembershipLevels | undefined | null
) =>
  membershipLevel === MembershipLevels.COT ||
  membershipLevel === MembershipLevels.TOT;

export const isRingWithInitials = (product: Product) =>
  product.title === ProductsWithInitials.ROUND_TABLE_RING;

export const getEvenNumber = (number: number) => {
  if (!number) return number;
  return number % 2 === 0 ? number : number + 1;
};

export const getPlaqueIncludedYearPlateCount = (type: PlaqueTypes) => {
  switch (type) {
    case PlaqueTypes.BRONZE_LIFE_MEMBER:
      return 10;
    default:
      return 1;
  }
};

export const getPlaqueTotalYearInsertCount = (type: PlaqueTypes) => {
  switch (type) {
    case PlaqueTypes.BRONZE_LIFE_MEMBER:
      return 24;
    default:
      return 1;
  }
};

export const isPlaqueWithMultipleIncludedYearPlates = (type: PlaqueTypes) =>
  type === PlaqueTypes.BRONZE_LIFE_MEMBER;

export const isPlaqueWithYearSelector = (type: PlaqueTypes) =>
  type !== PlaqueTypes.WHOLE_PERSON &&
  type !== PlaqueTypes.FIFTY_YEAR &&
  type !== PlaqueTypes.DESK_NAME_PLATE &&
  type !== PlaqueTypes.P1309_COMMEMORATIVE_DESK_NAME_PLATE &&
  type !== PlaqueTypes.HONOR_ROLL &&
  type !== PlaqueTypes.HONOR_ROLL_YEAR_PLATE_INSERT &&
  type !== PlaqueTypes.QUARTER_CENTURY &&
  type !== PlaqueTypes.QUARTER_CENTURY_YEAR_PLATE_INSERT &&
  type !== PlaqueTypes.BRONZE_LIFE_MEMBER;
